// 目前範圍內的設備樹（範圍見 lib/scope.tsx）。
// 哪些設備、每台能做什麼：GET /v1/me/devices（三種來源合併去重，SPEC #me-devices）——不在「我的設備」裡的不列，按鈕只看 access.actions。
// 樹與在線狀態來自 GET /v1/homes/{id}；能力清單與媒體狀態另從 VMS façade GET /v1/homes/{id}/vms/devices 補上（plan/vms-system-v0.md §4）。
// 在線狀態：/v1/vms/stream 的 presence 幀比家戶資料新就以幀為準（SPEC W24）；NVR 通道沒有自己的幀，照樣跟 parent。
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { getShadow, vms, type HomeThingBinding, type VmsFacadeDevice } from '@kinio/web-sdk';
import { useHomeDetailsOf, HOME_POLL_MS, HOME_POLL_STREAM_MS, type HomeResult } from '../features/devices/useHomeDetails';
import { buildDeviceTree, camerasOf, modelNameOf, parseChildAid, type DeviceAccess, type VmsDevice } from './devices';
import type { MyDevice } from './myDevices';
import type { ScopeHome } from './planeApi';
import { useScope } from './scope';
import { effectiveClusters, parseChannelAbilities } from './caps';
import { useLiveEvents, type LivePresence } from './liveEvents';
import { parseTtlTarget } from '../features/devices/ttlTarget';

export interface DevicesState {
  results: HomeResult[];
  tree: VmsDevice[];
  cameras: VmsDevice[];
  loading: boolean;
  /** façade（能力 / 媒體狀態 / live_limit）還在第一次載入；失敗不算載入中。 */
  facadeLoading: boolean;
}

/** 能力 = thing 的生效能力（capabilities.effective；SPEC #contract-app-ui，Eddy 2026-10-04）。
 * shadow reported 的 cluster（reported_clusters）不併進來——它不受能力上限管，併進來就繞過了上限（UX-11）；
 * 只有從沒宣告能力的舊設備（capsDeclared = false）控制鈕才退回看它（lib/caps.ts controlCaps）。
 * façade 有這台就一定回陣列（可能是空的）——「知道沒有」跟「還不知道」（undefined）要分開。 */
export function capsOf(d: VmsFacadeDevice): string[] {
  return effectiveClusters(d.capabilities);
}

/** façade storage_kinds（錄影碟 kind 去重）；不是陣列（子設備 / 舊 façade）→ undefined。 */
function storageKindsOf(v: unknown): string[] | undefined {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined;
}

/** façade 的設備資訊併進樹（依 device_ref）。 */
export function withFacade(tree: VmsDevice[], byHome: Map<string, VmsFacadeDevice[]>): VmsDevice[] {
  const apply = (d: VmsDevice): VmsDevice => {
    const f = byHome.get(d.homeId)?.find((x) => x.device_ref === d.aid);
    const kids = d.children.map(apply);
    if (!f) return kids === d.children ? d : { ...d, children: kids };
    return {
      ...d,
      capabilities: capsOf(f),
      capsDeclared: (f as Record<string, unknown>).capabilities_declared === true,
      reportedClusters: Array.isArray(f.reported_clusters) ? f.reported_clusters.filter((x): x is string => typeof x === 'string') : undefined,
      storageKinds: storageKindsOf((f as Record<string, unknown>).storage_kinds),
      channelAbilities: d.parentAid ? parseChannelAbilities((f as Record<string, unknown>).channel_abilities) : undefined,
      mediaStatus: typeof f.media_status === 'string' ? f.media_status : undefined,
      typeCode: d.typeCode ?? (typeof f.type_code === 'string' && f.type_code ? f.type_code : undefined),
      deviceClass: typeof f.device_class === 'string' && f.device_class ? f.device_class : undefined,
      liveLimit: typeof f.live_limit === 'number' && f.live_limit > 0 ? f.live_limit : undefined,
      presenceReason:
        typeof (f.presence as { reason?: unknown } | undefined)?.reason === 'string' ? ((f.presence as { reason: string }).reason as string) : undefined,
      // Kinio Node 的 UART1 接線（NODE-06）：只有頂層才有；沒帶這個鍵 = Node 沒回報（null）
      ttlTarget: d.parentAid ? undefined : parseTtlTarget((f as Record<string, unknown>).ttl_target),
      certNotAfter: typeof (f as Record<string, unknown>).cert_not_after === 'string' ? ((f as Record<string, unknown>).cert_not_after as string) : undefined,
      // 機種顯示名：façade 有就用 façade 的（每筆都帶，沒有為 null），否則留家戶清單帶的
      ...(() => {
        const m = modelNameOf(f as Record<string, unknown>);
        return m.modelName || m.modelSource ? m : {};
      })(),
      children: kids,
    };
  };
  return tree.map(apply);
}

/** shadow reported → 工位模式：kinio_station.info.seat_mode 要是 true 才算；沒報 / 不是布林 → undefined（照一般規則）。 */
export function seatModeOf(reported: Record<string, unknown> | undefined): boolean | undefined {
  const info = ((reported?.kinio_station ?? null) as { info?: { seat_mode?: unknown } } | null)?.info;
  return typeof info?.seat_mode === 'boolean' ? info.seat_mode : undefined;
}

/**
 * 要讀 shadow 看工位模式的設備（STN-01 工位模式）：頂層、而且 shadow 報過 kinio_station（façade reported_clusters），
 * façade 還沒回來時看 TYPE KST。其他設備不讀（Station 台數少，不會變成每台都打 shadow）。
 */
export function seatCandidates(tree: VmsDevice[]): VmsDevice[] {
  return tree.filter((d) => !d.parentAid && (d.reportedClusters ? d.reportedClusters.includes('kinio_station') : d.typeCode === 'KST'));
}

/** 工位模式併進樹（key = VmsDevice.key）；只動頂層。 */
export function withSeatMode(tree: VmsDevice[], seat: Map<string, boolean | undefined>): VmsDevice[] {
  if (!seat.size) return tree;
  return tree.map((d) => {
    const v = seat.get(d.key);
    return v === undefined || v === d.seatMode ? d : { ...d, seatMode: v };
  });
}

/**
 * presence 幀蓋到綁定列上（幀比 updatedAt 新才算）：state 照幀（dormant / asleep 跟 GET 一樣不算 online）、
 * 離線時 last_seen_at = 幀的時間、原因放 presence.reason（withFacade 之後再補到 presenceReason）。
 */
export function withLivePresence(homeId: string, things: HomeThingBinding[], live: Map<string, LivePresence>, updatedAt: number): HomeThingBinding[] {
  if (!live.size) return things;
  return things.map((b) => {
    // 子設備（NVR 通道等）沒有自己的連線事件：parent 離線 → 通道一定看不到；parent 在線不代表通道在線（B-17：
    // 通道自己的狀態 channel_offline 要留著，不然狀態點變綠、格子卻是「通道離線」）
    const own = live.get(`${homeId}/${b.device_ref}`);
    const parent = own ? undefined : live.get(`${homeId}/${parseChildAid(b.device_ref)?.parent ?? ''}`);
    const p = own ?? (parent && (parent.state !== 'online' || !b.thing?.presence?.state) ? parent : undefined);
    if (!p || p.at <= updatedAt) return b;
    const prev = b.thing?.presence;
    const at = new Date(p.at).toISOString();
    return {
      ...b,
      thing: {
        ...b.thing,
        presence: { ...prev, state: p.state, reason: p.reason, ...(p.state === 'offline' ? { last_seen_at: at, offline_at: at } : { connected_at: at }) },
      },
    };
  });
}

/**
 * 「我的設備」的權限併進樹，並只留範圍內的設備（scoped = 範圍內的「我的設備」）：
 * 不在裡面的本體與通道都拿掉；通道的 actions 來自 parent 的 children[]、can_manage 跟 parent。
 */
export function withAccess(tree: VmsDevice[], scoped: MyDevice[]): VmsDevice[] {
  const byKey = new Map(scoped.map((d) => [`${d.home_id}/${d.device_ref}`, d]));
  const out: VmsDevice[] = [];
  for (const d of tree) {
    const me = byKey.get(d.key);
    if (!me) continue;
    const base: Omit<DeviceAccess, 'actions'> = {
      source: me.source,
      can_manage: me.can_manage,
      denied: me.denied ?? [],
      expires_at: me.expires_at,
      share: me.share,
      other_sources: me.other_sources ?? [],
      container_only: me.container_only,
    };
    const children: VmsDevice[] = [];
    for (const c of d.children) {
      const mc = (me.children ?? []).find((x) => x.device_ref === c.aid);
      if (!mc) continue;
      children.push({ ...c, access: { ...base, container_only: false, denied: [], actions: mc.actions ?? [] } });
    }
    // 沒取名的設備用平台的 display_name（設備名稱單一來源：房間 + 種類、種類 + 末 4 碼），樹、清單、⌘K、稽核、範圍外的節點同一個名字
    // （SPEC #vms-web 機種與名稱一個函式「使用者取的名字 → 平台的 display_name → 機種友善名＋末 4 碼」；第七輪 B7-07）
    const name = d.autoName && me.display_name ? me.display_name : d.name;
    out.push({ ...d, name, children, access: { ...base, actions: me.container_only ? [] : (me.actions ?? []).map((a) => a.action) } });
  }
  return out;
}

export function useDevices(): DevicesState {
  const { visible, visibleDevices } = useScope();
  return useDevicesIn(visible, visibleDevices);
}

/**
 * 深連結（P0-2、SPEC #vms-web-220）：設備 / 事件網址不受目前範圍限制。目前範圍看得到就用範圍的樹；
 * 看不到但還在「我的設備」裡 → 直接查那個家戶（outOfScope = true，頁面提示可以切範圍）。
 * aid 給了就看那台（或它的 parent）在不在範圍裡；沒給就看家戶。
 */
export function useDevicesForHome(homeId: string, aid?: string): DevicesState & { outOfScope: boolean } {
  const cur = useDevices();
  const { visible, visibleDevices, homes, devices, loading } = useScope();
  const parent = aid ? (parseChildAid(aid)?.parent ?? aid) : undefined;
  const inScope = aid ? visibleDevices.some((d) => d.home_id === homeId && (d.device_ref === aid || d.device_ref === parent)) : visible.some((h) => h.home_id === homeId);
  const need = !inScope && !loading && homes.some((h) => h.home_id === homeId);
  const hs = useMemo(() => (need ? homes.filter((h) => h.home_id === homeId) : []), [need, homes, homeId]);
  const ds = useMemo(() => (need ? devices.filter((d) => d.home_id === homeId) : []), [need, devices, homeId]);
  const other = useDevicesIn(hs, ds);
  return need ? { ...other, outOfScope: true } : { ...cur, loading: cur.loading || loading, outOfScope: false };
}

export function useDevicesIn(homes: ScopeHome[], visibleDevices: MyDevice[]): DevicesState {
  const results = useHomeDetailsOf(homes);
  const { presence: live, state: streamState } = useLiveEvents();
  const facade = useQueries({
    queries: results.map((r) => ({
      queryKey: ['vms-devices', r.home.home_id],
      queryFn: () => vms.allDevices(r.home.endpoints.api, r.home.home_id),
      staleTime: 30_000,
      refetchInterval: streamState === 'open' ? HOME_POLL_STREAM_MS : HOME_POLL_MS * 2,
      retry: false,
    })),
  });
  const facadeTree = useMemo(() => {
    const base = results.flatMap((r) => (r.detail ? buildDeviceTree(r.home, withLivePresence(r.home.home_id, r.detail.things, live, r.updatedAt)) : []));
    const byHome = new Map<string, VmsFacadeDevice[]>();
    results.forEach((r, i) => {
      if (facade[i]?.data) byHome.set(r.home.home_id, facade[i].data!);
    });
    const merged = withAccess(byHome.size ? withFacade(base, byHome) : base, visibleDevices);
    if (!live.size) return merged;
    // façade 的 presence.reason 可能比幀舊：有比較新的幀就用幀的原因
    const fresh = (d: VmsDevice): VmsDevice => {
      const r = results.find((x) => x.home.home_id === d.homeId);
      const pp = d.parentAid ? live.get(`${d.homeId}/${d.parentAid}`) : undefined;
      // 通道：parent 在線的幀不蓋掉通道自己的離線原因（B-17）
      const p = live.get(d.key) ?? (pp && (pp.state !== 'online' || d.presence !== 'offline') ? pp : undefined);
      const kids = d.children.map(fresh);
      if (!p || p.at <= (r?.updatedAt ?? 0)) return kids.some((k, i) => k !== d.children[i]) ? { ...d, children: kids } : d;
      return { ...d, presenceReason: p.state === 'offline' ? p.reason : undefined, children: kids };
    };
    return merged.map(fresh);
  }, [results, facade, live, visibleDevices]);
  // 工位模式（STN-01）：Station 的 shadow kinio_station.info.seat_mode；跟設備頁 / 總覽共用 ['shadow', home, aid] 快取
  const seatDevs = useMemo(() => seatCandidates(facadeTree), [facadeTree]);
  const seatShadows = useQueries({
    queries: seatDevs.map((d) => ({
      queryKey: ['shadow', d.homeId, d.aid],
      queryFn: () => getShadow(d.apiEndpoint, d.homeId, d.aid),
      staleTime: 30_000,
      refetchInterval: 60_000,
      retry: false,
    })),
  });
  const seatKey = seatShadows.map((q) => String(seatModeOf(q.data?.state?.reported as Record<string, unknown> | undefined))).join(',');
  const tree = useMemo(
    () => withSeatMode(facadeTree, new Map(seatDevs.map((d, i) => [d.key, seatModeOf(seatShadows[i]?.data?.state?.reported as Record<string, unknown> | undefined)]))),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seatShadows 每次 render 都是新陣列，用 seatKey 判有沒有變
    [facadeTree, seatDevs, seatKey],
  );
  const cameras = useMemo(() => camerasOf(tree), [tree]);
  return { results, tree, cameras, loading: results.some((r) => r.loading), facadeLoading: facade.some((q) => q.isPending && q.fetchStatus !== 'idle') };
}
